import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type { AdElement, Animation, ElementTool, NewElementSpec } from '@adflow/types';

import {
  applyDocumentLayoutToAdElements,
  documentFromElements,
  withElementPositions,
} from '../canvas/document';
import {
  beginDrag,
  cancelDrag,
  commitDrag,
  exceedsDragThreshold,
  previewDragPosition,
  updateDragFromScreenDelta,
  type DragDraft,
} from '../canvas/gesture';
import {
  hitTargetsFromElements,
  isSameSelection,
  normalizeRect,
  pickTargetsInRect,
  pickTopmostAtPoint,
  selectionOnPress,
  type HitTarget,
} from '../canvas/selection';
import type { Point } from '../canvas/viewport';
import StoreContext from '../contexts/EditorContext';
import { rectFromGesture } from '../elements/newElements';
import { applyCommittedDragToRegistry } from '../operations/canvasDragCommit';
import { useTemplateOperationExtras } from '../operations/operationExtrasContext';
import type { EditorCanvasHistory } from '../store/history';
import type { CanvasViewport } from './useCanvasViewport';

export type CanvasGesture =
  | {
      kind: 'draw';
      tool: ElementTool;
      originStage: Point;
      currentStage: Point;
      originClient: Point;
      dragged: boolean;
      square: boolean;
    }
  | {
      kind: 'marquee';
      originStage: Point;
      currentStage: Point;
      originClient: Point;
      /** Selection to merge into on release when the marquee is additive. */
      baseSelection: string[];
      additive: boolean;
      /**
       * Pressed on empty space with nothing selected: a click, or a drag over a zone with no
       * elements in it, makes a new text element instead of selecting.
       */
      createsText: boolean;
    }
  | {
      kind: 'drag';
      draft: DragDraft;
      originClient: Point;
      /** Flips once the pointer crosses the drag threshold. */
      engaged: boolean;
      collapseToId: string | null;
    };

export type UseCanvasGesturesArgs = {
  camera: Pick<CanvasViewport, 'viewport' | 'clientToStage' | 'beginPan' | 'movePan' | 'endPan'>;
  elements: AdElement[];
  resolvedElements: AdElement[];
  animation: Animation | null;
  selectedElementIds: string[];
  onSelectionChange: (elementIds: string[]) => void;
  history?: EditorCanvasHistory;
  activeTool: ElementTool | null;
  onActiveToolChange?: (tool: ElementTool | null) => void;
  onInsertElement?: (spec: NewElementSpec) => void;
};

export type CanvasGestures = {
  gesture: CanvasGesture | null;
  displayArtwork: AdElement[];
  hitTargets: HitTarget[];
  hoveredElementId: string | null;
  /**
   * Escape. A drag is kept as a cancelled draft until the pointer is released, so its
   * elements snap back and the release commits nothing; any other gesture is dropped.
   */
  cancelGesture: () => void;
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: (event: ReactPointerEvent) => void;
  onPointerCancel: (event: ReactPointerEvent) => void;
  onPointerLeave: () => void;
};

function clientPoint(event: ReactPointerEvent): Point {
  return { x: event.clientX, y: event.clientY };
}

export function useCanvasGestures({
  camera,
  elements,
  resolvedElements,
  animation,
  selectedElementIds,
  onSelectionChange,
  history,
  activeTool,
  onActiveToolChange,
  onInsertElement,
}: UseCanvasGesturesArgs): CanvasGestures {
  const { viewport, clientToStage, beginPan, movePan, endPan } = camera;
  const editorContext = useContext(StoreContext);
  const operationExtras = useTemplateOperationExtras();

  // The ref is read by handlers between renders; the state drives the overlays.
  const gestureRef = useRef<CanvasGesture | null>(null);
  const [gesture, setGestureState] = useState<CanvasGesture | null>(null);
  const [hoveredElementId, setHoveredElementId] = useState<string | null>(null);
  const pointerRef = useRef<{ pointerId: number; last: Point } | null>(null);

  const setGesture = useCallback((next: CanvasGesture | null) => {
    gestureRef.current = next;
    setGestureState(next);
  }, []);

  const document = useMemo(() => documentFromElements(elements, animation), [animation, elements]);
  const draft = gesture?.kind === 'drag' ? gesture.draft : null;

  const displayArtwork = useMemo(() => {
    const preview: Record<string, Point> = {};
    if (draft && !draft.cancelled) {
      for (const elementId of draft.elementIds) {
        const position = previewDragPosition(draft, elementId);
        if (position) {
          preview[elementId] = position;
        }
      }
    }
    return applyDocumentLayoutToAdElements(
      resolvedElements,
      withElementPositions(document, preview),
    ).filter((element) => element.hidden !== true);
  }, [document, draft, resolvedElements]);

  const hitTargets = useMemo(() => hitTargetsFromElements(displayArtwork), [displayArtwork]);

  const capturePointer = useCallback((event: ReactPointerEvent) => {
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // Cypress and other test runners may synthesize pointer events without an active pointer.
    }
    pointerRef.current = { pointerId: event.pointerId, last: clientPoint(event) };
  }, []);

  const releasePointer = useCallback((event: ReactPointerEvent) => {
    pointerRef.current = null;
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Ignore release failures when capture was never established.
    }
  }, []);

  const pointerDeltaSinceLastMove = useCallback((event: ReactPointerEvent) => {
    const pointer = pointerRef.current;
    if (!pointer || pointer.pointerId !== event.pointerId) {
      return null;
    }
    pointerRef.current = { ...pointer, last: clientPoint(event) };
    return { x: event.clientX - pointer.last.x, y: event.clientY - pointer.last.y };
  }, []);

  const cancelGesture = useCallback(() => {
    const current = gestureRef.current;
    if (!current) {
      return;
    }
    pointerRef.current = null;
    if (current.kind === 'drag') {
      setGesture({ ...current, draft: cancelDrag(current.draft), collapseToId: null });
      return;
    }
    setGesture(null);
  }, [setGesture]);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (event.button !== 0) {
        return;
      }
      if (beginPan(event)) {
        capturePointer(event);
        return;
      }

      const stagePoint = clientToStage(event.clientX, event.clientY);
      const originClient = clientPoint(event);

      if (activeTool && onInsertElement) {
        // An armed tool owns the gesture: it never selects or drags what is under the pointer.
        setGesture({
          kind: 'draw',
          tool: activeTool,
          originStage: stagePoint,
          currentStage: stagePoint,
          originClient,
          dragged: false,
          square: event.shiftKey,
        });
        setHoveredElementId(null);
        capturePointer(event);
        return;
      }

      const picked = pickTopmostAtPoint(hitTargets, stagePoint, { includeLocked: true });
      const additive = event.shiftKey || event.metaKey || event.ctrlKey;

      if (!picked) {
        // Empty space: start a marquee. Selection (including clearing) is applied on release.
        setGesture({
          kind: 'marquee',
          originStage: stagePoint,
          currentStage: stagePoint,
          originClient,
          baseSelection: additive ? selectedElementIds : [],
          additive,
          createsText: !additive && selectedElementIds.length === 0 && onInsertElement != null,
        });
        capturePointer(event);
        return;
      }

      const press = selectionOnPress({
        elements,
        targets: hitTargets,
        selected: selectedElementIds,
        picked,
        additive,
      });
      if (!isSameSelection(press.selection, selectedElementIds)) {
        onSelectionChange(press.selection);
      }
      if (!press.startsDrag) {
        return;
      }

      // Locked members stay where they are. Hidden members still move so the group keeps its
      // relative layout, which is why this reads from `elements` rather than the hit targets.
      const layoutById = new Map(document.elements.map((layout) => [layout.id, layout]));
      const movableIds: string[] = [];
      const startPositions: Record<string, Point> = {};
      for (const id of press.selection) {
        const element = elements.find((entry) => entry.id === id);
        const layout = layoutById.get(id);
        if (element && element.locked !== true && layout) {
          movableIds.push(id);
          startPositions[id] = { x: layout.x, y: layout.y };
        }
      }

      setGesture({
        kind: 'drag',
        draft: beginDrag(movableIds, startPositions),
        originClient,
        engaged: false,
        collapseToId: press.collapseToId,
      });
      capturePointer(event);
    },
    [
      activeTool,
      beginPan,
      capturePointer,
      clientToStage,
      document.elements,
      elements,
      hitTargets,
      onInsertElement,
      onSelectionChange,
      selectedElementIds,
      setGesture,
    ],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      if (movePan(event)) {
        setHoveredElementId(null);
        return;
      }

      const current = gestureRef.current;
      if (current?.kind === 'draw') {
        setGesture({
          ...current,
          currentStage: clientToStage(event.clientX, event.clientY),
          dragged:
            current.dragged || exceedsDragThreshold(current.originClient, clientPoint(event)),
          square: event.shiftKey,
        });
        return;
      }

      if (activeTool) {
        setHoveredElementId(null);
        return;
      }

      if (current?.kind === 'marquee') {
        // Selection is applied on release so the sidebar doesn't resize the viewport mid-drag.
        setGesture({ ...current, currentStage: clientToStage(event.clientX, event.clientY) });
        setHoveredElementId(null);
        return;
      }

      if (current?.kind === 'drag' && !current.draft.cancelled) {
        setHoveredElementId(null);
        if (!current.engaged && !exceedsDragThreshold(current.originClient, clientPoint(event))) {
          return;
        }
        // The pointer still sits at the press point until engaged, so the first delta carries the
        // full travel.
        const delta = pointerDeltaSinceLastMove(event);
        const moved = delta != null && (delta.x !== 0 || delta.y !== 0);
        if (current.engaged && !moved) {
          return;
        }
        setGesture({
          ...current,
          engaged: true,
          draft: moved
            ? updateDragFromScreenDelta(current.draft, delta, viewport.zoom)
            : current.draft,
        });
        return;
      }

      const stagePoint = clientToStage(event.clientX, event.clientY);
      setHoveredElementId(pickTopmostAtPoint(hitTargets, stagePoint, { includeLocked: true }));
    },
    [
      activeTool,
      clientToStage,
      hitTargets,
      movePan,
      pointerDeltaSinceLastMove,
      setGesture,
      viewport.zoom,
    ],
  );

  const onPointerLeave = useCallback(() => {
    setHoveredElementId(null);
  }, []);

  const finishPointer = useCallback(
    (event: ReactPointerEvent) => {
      endPan();
      releasePointer(event);
      const finished = gestureRef.current;
      if (!finished) {
        return;
      }
      setGesture(null);
      const isPointerCancel = event.type === 'pointercancel';

      if (finished.kind === 'draw') {
        // Elements can be placed anywhere on the pasteboard, including outside the frame, since
        // they get moved onto it afterwards.
        if (!isPointerCancel) {
          const rect = rectFromGesture(finished.tool, finished.originStage, finished.currentStage, {
            dragged: finished.dragged,
            square: event.shiftKey || finished.square,
          });
          // Placing one element puts the tool away, like Figma.
          onActiveToolChange?.(null);
          onInsertElement?.({ tool: finished.tool, rect });
        }
        return;
      }

      if (finished.kind === 'drag') {
        if (finished.draft.cancelled) {
          return;
        }
        const after = commitDrag(finished.draft);
        if (after) {
          const before: Record<string, Point> = {};
          for (const elementId of finished.draft.elementIds) {
            const start = finished.draft.startPositions[elementId];
            if (start) {
              before[elementId] = { ...start };
            }
          }
          history?.onMoveCommit(before, after);
          const store = editorContext.store;
          if (store) {
            void applyCommittedDragToRegistry(
              store,
              finished.draft,
              withElementPositions(document, after),
              operationExtras,
            );
          }
        } else if (finished.collapseToId) {
          onSelectionChange([finished.collapseToId]);
        }
        return;
      }

      const hits = pickTargetsInRect(
        hitTargets,
        normalizeRect(finished.originStage, finished.currentStage),
      );
      const dragged = exceedsDragThreshold(finished.originClient, clientPoint(event));
      // A click on the canvas, or a drag over a zone with nothing in it, adds text there.
      const addsText = !isPointerCancel && finished.createsText && (!dragged || hits.length === 0);
      if (addsText) {
        onInsertElement?.({
          tool: 'TEXT',
          rect: rectFromGesture('TEXT', finished.originStage, finished.currentStage, { dragged }),
        });
        return;
      }
      const selection = finished.additive
        ? [...finished.baseSelection, ...hits.filter((id) => !finished.baseSelection.includes(id))]
        : hits;
      if (!isSameSelection(selection, selectedElementIds)) {
        onSelectionChange(selection);
      }
    },
    [
      document,
      editorContext.store,
      endPan,
      history,
      hitTargets,
      onActiveToolChange,
      onInsertElement,
      onSelectionChange,
      operationExtras,
      releasePointer,
      selectedElementIds,
      setGesture,
    ],
  );

  return {
    gesture,
    displayArtwork,
    hitTargets,
    hoveredElementId,
    cancelGesture,
    onPointerDown,
    onPointerMove,
    onPointerUp: finishPointer,
    onPointerCancel: finishPointer,
    onPointerLeave,
  };
}
